import { ConversationDetails } from "@virtualprogaming/js-public-sdk";
import React, { FC, useCallback, useMemo } from "react";
import { FormattedMessage, useIntl } from "react-intl";

import {
  useChatConversationMarkUnread,
  useChatDeleteConversation,
  useChatDeleteSystemConversation,
  useChatLeaveConversation,
  useChatPinConversation,
  useChatUnpinConversation,
} from "~/queries";
import {
  SelectedWindows,
  useChatAuthAccessToken,
  useChatAuthUserData,
  useChatSelectedWindow,
  useChatToRename,
  useSelectedChat,
  useToast,
} from "~/ui/context";
import { useCurrentUser } from "~/utils";

import { ConversationAction } from "./ConversationAction";

export const ConversationActions: FC<{
  conversation: ConversationDetails;
  onRead: (id: number | undefined, last_message_id: number | undefined) => void;
  isReadingChat: boolean;
}> = ({ conversation, onRead, isReadingChat }) => {
  const accessToken = useChatAuthAccessToken();
  const { setChatToRename } = useChatToRename();
  const { setSelectedChat } = useSelectedChat();
  const { setSelectedWindow } = useChatSelectedWindow();
  const { chatAuthUser } = useChatAuthUserData();
  const intl = useIntl();
  const toast = useToast();
  const currentUser = useCurrentUser();

  const {
    mutateAsync: deleteConversation,
    isLoading: isConversationDeleting,
  } = useChatDeleteConversation();
  const {
    mutateAsync: deleteConversationSystem,
    isLoading: isConversationDeletingSystem,
  } = useChatDeleteSystemConversation();
  const {
    mutateAsync: pinConversation,
    isLoading: isPinningChat,
  } = useChatPinConversation();
  const {
    mutateAsync: unpinConversation,
    isLoading: isUnpinningChat,
  } = useChatUnpinConversation();
  const {
    mutateAsync: unreadConversation,
    isLoading: isUnreadingChat,
  } = useChatConversationMarkUnread();

  const {
    mutateAsync: leaveConversation,
    isLoading: isLeavingChat,
  } = useChatLeaveConversation();

  const globalTag = useMemo(
    () => conversation?.tags?.includes("global") ?? false,
    [conversation]
  );

  const handleDeleteConversation = useCallback(
    (id: number | undefined) => {
      if (!id) return;
      if (
        confirm(
          intl.formatMessage({
            defaultMessage:
              "Are you sure you want to delete this conversation?",
            id: "47WSnT",
          })
        )
      ) {
        if (globalTag && currentUser?.user_id === 3) {
          deleteConversationSystem([id]).then(() => {
            toast({
              message: intl.formatMessage({
                defaultMessage:
                  "You have successfully deleted the conversation.",
                id: "D3PBe0",
              }),
            });
            setSelectedChat(null);
            setSelectedWindow(SelectedWindows.None);
          });
        } else {
          deleteConversation([id, accessToken]).then(() => {
            toast({
              message: intl.formatMessage({
                defaultMessage:
                  "You have successfully deleted the conversation.",
                id: "D3PBe0",
              }),
            });
            setSelectedChat(null);
            setSelectedWindow(SelectedWindows.None);
          });
        }
      }
    },
    [
      intl,
      globalTag,
      currentUser,
      deleteConversationSystem,
      toast,
      setSelectedChat,
      setSelectedWindow,
      deleteConversation,
      accessToken,
    ]
  );

  const handlePinConversation = useCallback(
    (id: number | undefined) => {
      if (!id) return;
      pinConversation([id, accessToken]);
    },
    [accessToken, pinConversation]
  );

  const handleUnpinConversation = useCallback(
    (id: number | undefined) => {
      if (!id) return;
      unpinConversation([id, accessToken]);
    },
    [accessToken, unpinConversation]
  );

  const handleUnreadConversation = useCallback(
    (id: number | undefined) => {
      if (!id) return;
      unreadConversation([id, accessToken]);
    },
    [accessToken, unreadConversation]
  );

  const handleRenameConversation = useCallback(
    (id: number | undefined) => {
      if (!id) return;
      setSelectedChat(id);
      setChatToRename(id);
      setSelectedWindow(SelectedWindows.Conversation);
    },
    [setChatToRename, setSelectedChat, setSelectedWindow]
  );
  const handleLeaveConversation = useCallback(
    (id: number | undefined) => {
      if (!id) return;
      if (!chatAuthUser?.user?.id) return;

      if (
        confirm(
          intl.formatMessage({
            defaultMessage: "Are you sure you want to leave this conversation?",
            id: "Ual5Vm",
          })
        )
      ) {
        leaveConversation([id, accessToken, [chatAuthUser.user.id]]).then(
          () => {
            setSelectedChat(null);
            setChatToRename(null);
            setSelectedWindow(SelectedWindows.None);
          }
        );
      }
    },
    [
      accessToken,
      chatAuthUser,
      intl,
      leaveConversation,
      setChatToRename,
      setSelectedChat,
      setSelectedWindow,
    ]
  );

  return (
    <div className="h-50px flex items-center justify-start w-100 mg-l-sm">
      {(conversation?.created_by_id === chatAuthUser?.user.id ||
        (globalTag && currentUser?.user_id === 3)) && (
        <ConversationAction
          onClick={(e) => {
            e?.stopPropagation();
            handleRenameConversation(conversation.id);
          }}
          icon={["fal", "pen"]}
          isLoading={false}
          name={<FormattedMessage defaultMessage="Rename" id="iXNbPf" />}
          className="right-10"
        />
      )}
      <ConversationAction
        onClick={(e) => {
          e?.stopPropagation();
          if (conversation?.is_pinned) {
            handleUnpinConversation(conversation.id);
          } else {
            handlePinConversation(conversation.id);
          }
        }}
        icon={[conversation?.is_pinned ? "fas" : "far", "thumbtack"]}
        isLoading={isPinningChat || isUnpinningChat}
        name={
          conversation?.is_pinned ? (
            <FormattedMessage defaultMessage="Unpin" id="opNVQC" />
          ) : (
            <FormattedMessage defaultMessage="Pin" id="puLNUJ" />
          )
        }
      />
      <ConversationAction
        onClick={(e) => {
          e?.stopPropagation();
          if (conversation?.is_unread) {
            onRead(conversation.id, conversation?.last_message?.id);
          } else {
            handleUnreadConversation(conversation.id);
          }
        }}
        icon={
          conversation?.is_unread
            ? ["far", "envelope-open"]
            : ["far", "envelope"]
        }
        isLoading={isReadingChat || isUnreadingChat}
        name={
          conversation?.is_unread ? (
            <FormattedMessage defaultMessage="Read" id="filwqD" />
          ) : (
            <FormattedMessage defaultMessage="Unread" id="jabB4C" />
          )
        }
      />

      <ConversationAction
        onClick={(e) => {
          e?.stopPropagation();
          handleLeaveConversation(conversation.id);
        }}
        icon={["fal", "sign-out"]}
        isLoading={isLeavingChat}
        name={<FormattedMessage defaultMessage="Leave" id="fnihsY" />}
        className="right-10"
      />
      {(conversation?.created_by_id === chatAuthUser?.user.id ||
        (globalTag && currentUser?.user_id === 3)) && (
        <ConversationAction
          onClick={(e) => {
            e?.stopPropagation();
            handleDeleteConversation(conversation.id);
          }}
          icon={["far", "trash"]}
          isLoading={isConversationDeleting || isConversationDeletingSystem}
          bg="bg-danger"
          name={<FormattedMessage defaultMessage="Delete" id="K3r6DQ" />}
          isDelete
        />
      )}
    </div>
  );
};
